import * as yup from "yup";

import {
  EarlyRepaymentValidation,
  FREE_EARLY_REPAYMENT,
} from "@krea/shared/features/early-repayment";
import type { Currency } from "@krea/shared/types/common";

import {
  AmortizationMethodEnum,
  BidTypeEnum,
  PaymentFrequency,
} from "@/features/common/enums";
import type { ICompanyLoanBidInput } from "@/features/lender-responses/types/lender-response";

import { AdministrationFeesValidation } from "../utils/validators/AdministrationFeesValidation";
import { AmountValidation } from "../utils/validators/AmountValidation";
import { LenderMonthlyAmountValidation } from "../utils/validators/LenderMonthlyAmountValidation";
import { LenderMonthlyInterestValidation } from "../utils/validators/LenderMonthlyInterestValidation";
import { PaybackTimeMonthsValidation } from "../utils/validators/PaybackTimeMonthsValidation";
import { PaymentFrequencyValidation } from "../utils/validators/PaymentFrequencyValidation";
import { SetupFeeValidation } from "../utils/validators/SetupFeeValidation";
import { TotalCostValidation } from "../utils/validators/TotalCostValidation";

export const companyLoanBidSchema: (
  currency: Currency,
  formatWithCurrency: (value: number) => string,
) => yup.ObjectSchema<ICompanyLoanBidInput> = (currency, formatWithCurrency) =>
  yup.object().shape({
    type: yup
      .string()
      .required("Type is required")
      .oneOf([BidTypeEnum.COMPANY_LOAN])
      .default(BidTypeEnum.COMPANY_LOAN),
    amount: AmountValidation(currency, formatWithCurrency),
    amortizationMethod: yup
      .string()
      .oneOf(Object.values(AmortizationMethodEnum))
      .required("Amortisation method is required")
      .default(AmortizationMethodEnum.ANNUITY),
    paymentFrequency: PaymentFrequencyValidation(),
    paybackTimeMonths: PaybackTimeMonthsValidation().max(
      120,
      (schema) => `Repayment time can't be more than ${schema.max} months`,
    ),
    lenderMonthlyInterest: LenderMonthlyInterestValidation({
      isFunction: (amortizationMethod: AmortizationMethodEnum) =>
        amortizationMethod !== AmortizationMethodEnum.FIXED,
    }),
    lenderMonthlyAmount: LenderMonthlyAmountValidation(
      currency,
      formatWithCurrency,
    ),
    totalCost: TotalCostValidation(formatWithCurrency),
    ...EarlyRepaymentValidation(),
    administrationFees: AdministrationFeesValidation(formatWithCurrency),
    setupFee: SetupFeeValidation(formatWithCurrency),
    monthlyCost: yup.number().nullable().min(0, "Should be at least 0"),
    monthlyCostWithoutSetupFee: yup
      .number()
      .nullable()
      .min(0, "Should be at least 0"),
  });

export const companyLoanBidDefaultValues: ICompanyLoanBidInput = {
  type: BidTypeEnum.COMPANY_LOAN,
  amortizationMethod: AmortizationMethodEnum.ANNUITY,
  paymentFrequency: PaymentFrequency.MONTHLY,
  administrationFees: undefined,
  amount: undefined,
  earlyRepayment: FREE_EARLY_REPAYMENT,
  lenderMonthlyInterest: undefined,
  paybackTimeMonths: undefined,
  setupFee: undefined,
  totalCost: undefined,
};
